import React, { useState } from "react";
import styled from "styled-components";
import { Menu } from "@mui/material";
import {
    IDesignValidationProvider,
    ValidationIssue,
    ValidationLevel,
} from "../../rule-engine-core/Common/IDesignValidationProvider";
import { ExclamationIcon } from "../../CommonUI/Icon/ExclamationIcon";
import { ExclamationRedIcon } from "../../CommonUI/Icon/ExclamationRedIcon";
import { WarningRedIcon } from "../../CommonUI/Icon/WarningRedIcon";
import { WarningIcon } from "../../CommonUI/Icon/WarningIcon";
import { UIStyleConstants } from "../../rule-engine-core/Common/UIStyleConstants";

interface ValidationInformationProps {
    provider: IDesignValidationProvider;
}

export const ValidationInformation = ({ provider }: ValidationInformationProps) => {
    let issues = provider.getDesignValidationIssues();

    if (issues.length === 0) return null;

    let errors: ValidationIssue[] = [];
    let warnings: ValidationIssue[] = [];

    for (let issue of issues) {
        if (issue.level === ValidationLevel.ERROR) {
            errors.push(issue);
        } else {
            warnings.push(issue);
        }
    }

    return (
        <Render>
            <Error issues={errors} />
            <Warning issues={warnings} />
        </Render>
    );
};

interface IError {
    issues: ValidationIssue[];
}

const Error = ({ issues }: IError) => {
    const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

    if (issues.length === 0) return null;

    const onOpen = (event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const onClose = () => {
        setAnchorEl(null);
    };

    return (
        <>
            <StyledButton
                type={ValidationLevel.ERROR}
                onClick={onOpen}
            >
                <ExclamationIcon alt="Error" />
                <span>{issues.length}</span>
            </StyledButton>
            <StyledMenu
                open={!!anchorEl}
                onClose={onClose}
                anchorOrigin={{ vertical: "top", horizontal: "left" }}
                anchorEl={anchorEl}
            >
                <IssueType type={ValidationLevel.ERROR}>Errors</IssueType>
                <IssueList>
                    {issues.map((issue, idx) => (
                        <IssueListItem key={idx}>
                            <ExclamationRedIcon /> <p>{issue.issue}</p>
                        </IssueListItem>
                    ))}
                </IssueList>
            </StyledMenu>
        </>
    );
};

const Warning = ({ issues }: IError) => {
    const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

    if (issues.length === 0) return null;

    const onOpen = (event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const onClose = () => {
        setAnchorEl(null);
    };

    return (
        <>
            <StyledButton
                type={ValidationLevel.WARNING}
                onClick={onOpen}
            >
                <WarningIcon alt="Error" />
                <span>{issues.length}</span>
            </StyledButton>
            <StyledMenu
                open={!!anchorEl}
                onClose={onClose}
                anchorOrigin={{ vertical: "top", horizontal: "left" }}
                anchorEl={anchorEl}
            >
                <IssueType type={ValidationLevel.WARNING}>Warnings</IssueType>
                <IssueList>
                    {issues.map((issue, idx) => (
                        <IssueListItem key={idx}>
                            <WarningRedIcon /> <p>{issue.issue}</p>
                        </IssueListItem>
                    ))}
                </IssueList>
            </StyledMenu>
        </>
    );
};

const Render = styled.div`
    position: absolute;
    bottom: 20px;
    left: 20px;
    display: flex;
    padding-left: 10px;
    padding-right: 10px;

    z-index: 999;
`;

const StyledButton = styled.div<{ type: ValidationLevel }>`
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 140%;
    width: 60px;
    /* identical to box height, or 20px */

    color: ${UIStyleConstants.WHITE_COLOR};

    text-align: center;

    background: ${(props) =>
        props.type === ValidationLevel.ERROR ? UIStyleConstants.RED_COLOR : UIStyleConstants.ORANGE_COLOR};

    border-radius: 14px;
    height: 28px;
    display: flex;
    align-items: center;

    min-width: 46px;

    cursor: pointer;

    justify-content: space-around;
`;

const StyledMenu = styled(Menu)`
    && {
        .MuiMenu-paper {
            border: 1px solid ${UIStyleConstants.GRAY_VERY_LIGHT_COLOR};
            box-sizing: border-box;
            box-shadow: 0px 6px 8px -2px ${UIStyleConstants.GRAY_VERY_LIGHT_COLOR};
            border-radius: 8px;
            padding: 16px;
            width: 280px;
        }

        .MuiMenu-list {
            padding: 0;
        }
    }
`;

const IssueList = styled.div`
    max-height: 290px;
    overflow: auto;
`;

const IssueListItem = styled.div`
    display: flex;
    //styleName: text/Inter Regular 12px;

    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 17px;
    letter-spacing: 0px;
    text-align: left;
    color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};

    img {
        margin-right: 12px;
    }

    border-bottom: 1px solid ${UIStyleConstants.GRAY_VERY_LIGHT_COLOR};

    &:last-child {
        border: none;
    }
`;

const IssueType = styled.div<{ type: ValidationLevel }>`
    //styleName: title/h3 Inter SemiBold 16px;

    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: 22px;
    letter-spacing: 0em;
    text-align: left;
    margin-bottom: 4px;

    color: ${(props) =>
        props.type === ValidationLevel.ERROR ? UIStyleConstants.RED_COLOR : UIStyleConstants.ORANGE_COLOR};
`;
